<template>
  <div
    v-if="!term"
    class="c-search-highlights-content"
  >
    <template v-if="latestSearchedTerms.length || isDesktop">
      <SearchTags
        title="buscas recentes"
        class="c-search-highlights-content__recent-terms"
        :cards="latestSearchedTerms"
        @choose-term="useTerm"
      >
        nenhuma<br> busca recente
      </SearchTags>
    </template>

    <div
      v-if="isFetchingSearchHihglights"
      class="c-search-highlights-sections"
    >
      <SearchTags
        :cards="searchTagsPlaceholder"
        class="c-search-highlights-section"
        is-loading
      />

      <SearchCards
        v-for="(section, index) in ['enjoei pro', 'lojas famosas']"
        :key="`placeholder-${index}`"
        class="c-search-highlights-section__cards"
        :title="section"
        :is-fetching="isFetchingSearchHihglights"
      />
    </div>

    <div
      v-else-if="searchHighlightsSections.length"
      class="c-search-highlights-sections"
    >
      <div
        v-for="(section, sectionIndex) in searchHighlightsSections"
        :key="`search-section-${sectionIndex}`"
        class="c-search-highlights-section"
      >
        <component
          :is="section.layout.name"
          v-for="(component, componentIndex) in section.components"
          :key="`search-component-${componentIndex}`"
          :title="component.title"
          :action="component.action"
          :cards="component.cards"
          :card-radius="section.layout['card-radius']"
          @choose-term="navigateToUrl"
        />
      </div>
    </div>
  </div>
  <div v-else>
    <ContextSelector
      :selected-context="selectedContext"
      @change-context="onContextChange"
    />

    <SearchSuggestions
      :term="term"
      :context="selectedContext"
      @choose-term="useTerm"
    />
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from 'vuex'
  import STORE_TYPES_SEARCH from '@/web/store/registered/search'

  import ContextSelector from '@/web/components/header/search/context-selector.vue'
  import SearchSuggestions from '@/web/components/header/search/search-suggestions.vue'
  import SearchTags from '@/web/components/header-search/layouts/search-tags.vue'
  import SearchCards from '@/web/components/header-search/layouts/search-cards.vue'
  import { RECENTLY_SEARCHED_TERMS_STORAGE_KEY } from '@/web/components/product-feed/constants'

  import getPath from '@/tools/get-path'
  import media from '@/tools/mixins/media'
  import { storage } from '@/tools/storage'
  import { createSecureAxios } from '@/tools/axios/axios'
  import { PRODUCTS_SEARCH_CONTEXT } from '@/tools/constants/contexts-for-metrics'

  export default {
    components: {
      ContextSelector,
      SearchSuggestions,
      SearchTags,
      SearchCards
    },
    mixins: [
      media
    ],
    data() {
      return {
        isFetchingSearchHihglights: true,
        searchHighlightsSections: [],
        productsSearchRef: PRODUCTS_SEARCH_CONTEXT.SEARCH_BAR_RECENT
      }
    },
    computed: {
      ...mapGetters({
        term: STORE_TYPES_SEARCH.GETTERS.GET_INPUT_TERM,
        selectedContext: STORE_TYPES_SEARCH.GETTERS.GET_SELECTED_CONTEXT,
        recentlySeachedTerms: STORE_TYPES_SEARCH.GETTERS.GET_RECENTLY_SEARCHED_TERMS
      }),
      latestSearchedTerms() {
        const recentlySeachedTerms = this.recentlySeachedTerms.length && [...this.recentlySeachedTerms]
          || storage.local.get(RECENTLY_SEARCHED_TERMS_STORAGE_KEY)
          || []

        return recentlySeachedTerms.map(term => ({ label: term, url: `/s?q=${term}` }))
      },
      searchTagsPlaceholder() {
        return new Array(6).fill().map(() => ({ label: '', url: ''}))
      }
    },
    created() {
      this.getSearchHighlights()
    },
    methods: {
      ...mapMutations({
        setSelectedContext: STORE_TYPES_SEARCH.MUTATIONS.SET_SELECTED_CONTEXT
      }),
      async getSearchHighlights() {
        const secureAxios = await createSecureAxios()
        const endpoint = getPath('search-highlights')

        secureAxios.get(endpoint)
          .then(response => { this.searchHighlightsSections = response.data?.content })
          .catch(() => { this.searchHighlightsSections = [] })
          .finally(() => { this.isFetchingSearchHihglights = false })
      },
      onContextChange(selectedContext) {
        this.setSelectedContext(selectedContext)
      },
      useTerm(suggestedTerm, context) {
        if (suggestedTerm.url) {
          let href = new URL(suggestedTerm.url, window.location.href)
          href.searchParams.set('ref', context)

          window.location.href = href
        } else {
          this.term = suggestedTerm.text
          this.$nextTick().then(() => this.submitForm())
        }
      },
      navigateToUrl(card) {
        if (card.url) window.location.href = card.url
      }
    }
  }
</script>

<style lang="scss" scoped>
  .c-search-highlights-content {
    display: flex;
    flex-direction: column;

    @include breakpoint(lg) {
      flex-direction: row;
    }

    &__recent-terms {
      @include breakpoint (lg) {
        min-width: 250px;
        margin-right: 59px;
      }

      @include breakpoint($until: lg) {
        margin-top: $spacing-stack-lg;
      }
    }
  }

  ::v-deep {
    .c-search-highlights-tags {
      @include breakpoint($until: lg) {
        padding: 0 20px;
      }
    }
  }

  .c-search-highlights-sections {
    display: flex;
    flex-direction: column;
    margin-bottom: 65px;

    @include breakpoint(lg) {
      flex-direction: row;
      margin-bottom: 0;
    }
  }

  .c-search-highlights-section {
    @include breakpoint($until: lg) {
      margin-top: $spacing-stack-lg;
    }

    @include breakpoint(lg) {
      min-width: 250px;
    }

    &__tags {
      @include breakpoint(lg) {
        width: 233px;
      }
    }

    &__cards {
      @include breakpoint(lg) {
        min-width: 205px;
      }
    }

    &:not(:last-child) {
      @include breakpoint(lg) {
        padding-right: 59px;
      }
    }
  }

</style>
